import { useRef, useState, type FormEvent } from 'react';
import { CalendarClock, Image as ImageIcon, Plus, Trash2, Upload } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import {
  PLACEMENTS,
  PLACEMENT_LABEL,
  useBanners,
  useDeleteBanner,
  useSaveBanner,
  useUploadMedia,
  type Banner,
  type BannerPlacement,
  type ScheduleState,
} from '@/lib/cms-api';
import { apiBaseUrl } from '@/lib/env';
import { cn, errorMessage, fieldErrors, formatDateTime } from '@/lib/utils';

/**
 * Banner administration (spec §39).
 *
 * The column that matters is **Showing**, not Active. The storefront filters on
 * `isActive` *and* the schedule window, so a banner can be switched on and
 * still invisible because its window has not opened — which is exactly when a
 * merchandiser concludes the save failed and makes a second one.
 *
 * Images carry no baked-in text: the heading, subtitle and CTA are fields here
 * and rendered over the image by the storefront, so one asset serves several
 * campaigns.
 */

const STATE_TONE: Record<ScheduleState, 'success' | 'warning' | 'danger' | 'neutral'> = {
  SHOWING: 'success',
  SCHEDULED: 'warning',
  EXPIRED: 'danger',
  INACTIVE: 'neutral',
};

const STATE_LABEL: Record<ScheduleState, string> = {
  SHOWING: 'Showing now',
  SCHEDULED: 'Scheduled',
  EXPIRED: 'Window closed',
  INACTIVE: 'Switched off',
};

export function BannersPage() {
  const [placement, setPlacement] = useState<BannerPlacement | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Banner | 'new' | null>(null);

  const banners = useBanners({ page, limit: 20, ...(placement ? { placement } : {}) });
  const remove = useDeleteBanner();

  return (
    <div>
      <PageHeader
        title="Banners"
        description="Merchandising slots on the storefront. Scheduling is a date, not a job."
        action={
          <Button onClick={() => setEditing('new')}>
            <Plus className="size-4" aria-hidden="true" />
            New banner
          </Button>
        }
      />

      {remove.isError && (
        <div className="mb-4">
          <ErrorAlert title="Could not delete that banner">{errorMessage(remove.error)}</ErrorAlert>
        </div>
      )}

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Placement">
          {(['', ...PLACEMENTS] as Array<BannerPlacement | ''>).map((value) => (
            <button
              key={value || 'all'}
              type="button"
              role="tab"
              aria-selected={placement === value}
              onClick={() => {
                setPlacement(value);
                setPage(1);
              }}
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                placement === value
                  ? 'bg-brand-600 text-white'
                  : 'text-ink-muted hover:bg-canvas hover:text-ink',
              )}
            >
              {value ? PLACEMENT_LABEL[value] : 'Every placement'}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {banners.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-16" />
            ))}
          </div>
        ) : banners.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load banners">{errorMessage(banners.error)}</ErrorAlert>
          </div>
        ) : (banners.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No banners here"
            description="Create one, or choose a different placement."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Banner</Th>
                <Th>Placement</Th>
                <Th>Showing</Th>
                <Th>Window</Th>
                <Th>Priority</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {banners.data?.items.map((banner) => (
                <tr key={banner.id} className="hover:bg-canvas/60">
                  <Td>
                    <div className="flex items-center gap-3">
                      <BannerThumb banner={banner} />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-ink">{banner.title}</p>
                        {banner.subtitle && (
                          <p className="truncate text-xs text-ink-subtle">{banner.subtitle}</p>
                        )}
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <Badge tone="brand">{PLACEMENT_LABEL[banner.placement]}</Badge>
                  </Td>
                  <Td>
                    <Badge tone={STATE_TONE[banner.scheduleState]}>
                      {STATE_LABEL[banner.scheduleState]}
                    </Badge>
                  </Td>
                  <Td className="text-xs text-ink-subtle">
                    {banner.startDate || banner.endDate ? (
                      <span className="flex items-center gap-1">
                        <CalendarClock className="size-3.5" aria-hidden="true" />
                        {banner.startDate ? formatDateTime(banner.startDate) : 'Always'} →{' '}
                        {banner.endDate ? formatDateTime(banner.endDate) : 'Always'}
                      </span>
                    ) : (
                      'Open-ended'
                    )}
                  </Td>
                  <Td className="text-sm text-ink-muted">{banner.priority}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditing(banner)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete ${banner.title}`}
                        onClick={() => {
                          if (window.confirm(`Delete "${banner.title}"? Switching it off is reversible; this is not.`)) {
                            remove.mutate(banner.id);
                          }
                        }}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {banners.data && banners.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {banners.data.pagination.total} banners · page {banners.data.pagination.page} of{' '}
              {banners.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!banners.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!banners.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <BannerDrawer banner={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

/** Storage returns a root-relative URL, so a preview needs the API's origin. */
function mediaSrc(url: string): string {
  return url.startsWith('/') ? `${apiBaseUrl.replace(/\/api\/v\d+$/, '')}${url}` : url;
}

function BannerThumb({ banner }: { banner: Banner }) {
  return (
    <div className="size-12 shrink-0 overflow-hidden rounded-lg border border-line bg-canvas">
      <img
        src={mediaSrc(banner.imageUrl)}
        alt=""
        className="size-full object-cover"
        // A missing asset is a seeded placeholder, not a page error.
        onError={(event) => {
          event.currentTarget.style.visibility = 'hidden';
        }}
      />
    </div>
  );
}

/* ==========================================================================
   Editor
   ========================================================================== */

type Draft = {
  title: string;
  subtitle: string;
  imageUrl: string;
  mobileImageUrl: string;
  altText: string;
  ctaText: string;
  ctaUrl: string;
  placement: BannerPlacement;
  startDate: string;
  endDate: string;
  priority: string;
  isActive: boolean;
};

const EMPTY_DRAFT: Draft = {
  title: '',
  subtitle: '',
  imageUrl: '',
  mobileImageUrl: '',
  altText: '',
  ctaText: '',
  ctaUrl: '',
  placement: 'HERO',
  startDate: '',
  endDate: '',
  priority: '0',
  isActive: true,
};

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in local time, not an ISO string. */
function toLocalInput(value: string | null): string {
  if (!value) return '';

  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;

  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

/** Mounted only while open and keyed by id, so its state resets by unmounting. */
function BannerDrawer({ banner, onClose }: { banner: Banner | 'new' | null; onClose: () => void }) {
  if (!banner) {
    return null;
  }

  const existing = banner === 'new' ? null : banner;

  return (
    <Drawer open onClose={onClose} title={existing ? 'Edit banner' : 'New banner'}>
      <BannerForm key={existing?.id ?? 'new'} banner={existing} onClose={onClose} />
    </Drawer>
  );
}

function BannerForm({ banner, onClose }: { banner: Banner | null; onClose: () => void }) {
  const save = useSaveBanner();
  const upload = useUploadMedia();
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Draft>(() =>
    banner
      ? {
          title: banner.title,
          subtitle: banner.subtitle ?? '',
          imageUrl: banner.imageUrl,
          mobileImageUrl: banner.mobileImageUrl ?? '',
          altText: banner.altText ?? '',
          ctaText: banner.ctaText ?? '',
          ctaUrl: banner.ctaUrl ?? '',
          placement: banner.placement,
          startDate: toLocalInput(banner.startDate),
          endDate: toLocalInput(banner.endDate),
          priority: String(banner.priority),
          isActive: banner.isActive,
        }
      : EMPTY_DRAFT,
  );

  const existing = banner;

  const errors = fieldErrors(save.error);
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    save.mutate(
      {
        ...(existing ? { id: existing.id } : {}),
        title: draft.title,
        subtitle: draft.subtitle || null,
        imageUrl: draft.imageUrl,
        mobileImageUrl: draft.mobileImageUrl || null,
        altText: draft.altText || null,
        ctaText: draft.ctaText || null,
        ctaUrl: draft.ctaUrl || null,
        placement: draft.placement,
        startDate: draft.startDate ? new Date(draft.startDate).toISOString() : null,
        endDate: draft.endDate ? new Date(draft.endDate).toISOString() : null,
        priority: Number(draft.priority) || 0,
        isActive: draft.isActive,
      },
      { onSuccess: onClose },
    );
  };

  return (
  <form className="space-y-4" onSubmit={handleSubmit}>
      {save.isError && (
        <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>
      )}

      <Field label="Title" htmlFor="banner-title" required error={errors.title}>
        <Input
          id="banner-title"
          value={draft.title}
          onChange={(event) => set({ title: event.target.value })}
          required
        />
      </Field>

      <Field label="Subtitle" htmlFor="banner-subtitle" error={errors.subtitle}>
        <Textarea
          id="banner-subtitle"
          rows={2}
          value={draft.subtitle}
          onChange={(event) => set({ subtitle: event.target.value })}
        />
      </Field>

      <Field
        label="Image"
        htmlFor="banner-image"
        required
        error={errors.imageUrl}
        hint="Upload an asset, or paste a path such as /static/content/hero.jpg."
      >
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              id="banner-image"
              value={draft.imageUrl}
              onChange={(event) => set({ imageUrl: event.target.value })}
              placeholder="/static/content/…"
              required
            />
            <Button
              type="button"
              variant="outline"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              <Upload className="size-4" aria-hidden="true" />
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>

          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';

              if (file) {
                upload.mutate(file, { onSuccess: (media) => set({ imageUrl: media.url }) });
              }
            }}
          />

          {upload.isError && (
            <p className="text-xs text-danger-600">{errorMessage(upload.error)}</p>
          )}

          {draft.imageUrl && (
            <div className="overflow-hidden rounded-lg border border-line bg-canvas">
              <img
                src={mediaSrc(draft.imageUrl)}
                alt=""
                className="max-h-32 w-full object-cover"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </Field>

      <Field
        label="Mobile image"
        htmlFor="banner-mobile"
        error={errors.mobileImageUrl}
        hint="Optional. Used below the mobile breakpoint."
      >
        <Input
          id="banner-mobile"
          value={draft.mobileImageUrl}
          onChange={(event) => set({ mobileImageUrl: event.target.value })}
        />
      </Field>

      <Field
        label="Alt text"
        htmlFor="banner-alt"
        error={errors.altText}
        hint="Describe the offer. Defaults to the title — the headline is part of the image."
      >
        <Input
          id="banner-alt"
          value={draft.altText}
          onChange={(event) => set({ altText: event.target.value })}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Button text" htmlFor="banner-cta-text" error={errors.ctaText}>
          <Input
            id="banner-cta-text"
            value={draft.ctaText}
            onChange={(event) => set({ ctaText: event.target.value })}
            placeholder="Shop now"
          />
        </Field>

        <Field
          label="Button link"
          htmlFor="banner-cta-url"
          error={errors.ctaUrl}
          hint="A site path, or a full https:// URL."
        >
          <Input
            id="banner-cta-url"
            value={draft.ctaUrl}
            onChange={(event) => set({ ctaUrl: event.target.value })}
            placeholder="/products"
          />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Placement" htmlFor="banner-placement" required>
          <Select
            id="banner-placement"
            value={draft.placement}
            onChange={(event) => set({ placement: event.target.value as BannerPlacement })}
          >
            {PLACEMENTS.map((value) => (
              <option key={value} value={value}>
                {PLACEMENT_LABEL[value]}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Priority"
          htmlFor="banner-priority"
          error={errors.priority}
          hint="Higher shows first."
        >
          <Input
            id="banner-priority"
            type="number"
            min={0}
            value={draft.priority}
            onChange={(event) => set({ priority: event.target.value })}
          />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Starts" htmlFor="banner-start" hint="Leave blank for immediately.">
          <Input
            id="banner-start"
            type="datetime-local"
            value={draft.startDate}
            onChange={(event) => set({ startDate: event.target.value })}
          />
        </Field>

        <Field
          label="Ends"
          htmlFor="banner-end"
          error={errors.endDate}
          hint="Leave blank for never."
        >
          <Input
            id="banner-end"
            type="datetime-local"
            value={draft.endDate}
            onChange={(event) => set({ endDate: event.target.value })}
          />
        </Field>
      </div>

      <Checkbox
        label="Active"
        checked={draft.isActive}
        onChange={(event) => set({ isActive: event.target.checked })}
      />

      <p className="flex items-start gap-1.5 text-xs text-ink-subtle">
        <ImageIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Active only means switched on. A banner inside no window shows immediately; one with a
        future start date waits.
      </p>

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create banner'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
